import { memo, useMemo, useRef } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import { defineMessages, FormattedMessage, useIntl } from '@atlas/text';
import Input from '@atlas/uicorn/lib/Input/Input';
import Select from '@atlas/uicorn/lib/Input/Select';
import countryNames from '@atlas/tool/box/countryNames';

export const messages = defineMessages({
    street1: { id: 'lblStreet1', defaultMessage: 'Street 1' },
    street2: { id: 'lblStreet2', defaultMessage: 'Street 2' },
    city: { id: 'lblCity', defaultMessage: 'City' },
    stateProvinceRegion: { id: 'lblStateProvinceRegion', defaultMessage: 'State/Province/Region' },
    zipCode: { id: 'lblZipCode', defaultMessage: 'ZIP Code' },
    country: { id: 'lblCountry', defaultMessage: 'Country' },
    enterStreet1: { id: 'lblEnterStreet1', defaultMessage: 'Enter street 1...' },
    enterStreet2: { id: 'lblEnterStreet2', defaultMessage: 'Enter street 2...' },
    enterCity: { id: 'lblEnterCity', defaultMessage: 'Enter city...' },
    enterState: { id: 'lblEnterState', defaultMessage: 'Enter state...' },
    enterZipCode: { id: 'lblEnterZipCode', defaultMessage: 'Enter ZIP code...' },
    selectCountry: { id: 'lblSelectCountry', defaultMessage: 'Select country' },
});

const AddressInput = memo(function AddressInput(props: TAddressInputProps): React.ReactElement {
    const intl = useIntl();
    const valueRef = useRef<CustomFieldAddressValue>({ ...props.value });

    const countryOptions = useMemo(
        () => Object.entries(countryNames).map(([code, name]) => ({ key: code, label: name })),
        [],
    );

    return (
        <>
            <SInputFormItemLabel $disabled={props.disabled}>
                <FormattedMessage {...messages.street1} />
            </SInputFormItemLabel>
            <Input
                disabled={props.disabled}
                placeholder={intl.formatMessage(messages.enterStreet1)}
                onChange={(e) => {
                    valueRef.current.street1 = e.target.value;
                    props.onChange?.({ ...valueRef.current, street1: e.target.value });
                }}
                value={valueRef.current.street1}
            />
            <SInputsSpacer />
            <SInputFormItemLabel $disabled={props.disabled}>
                <FormattedMessage {...messages.street2} />
            </SInputFormItemLabel>
            <Input
                disabled={props.disabled}
                placeholder={intl.formatMessage(messages.enterStreet2)}
                onChange={(e) => {
                    valueRef.current.street2 = e.target.value;
                    props.onChange?.({ ...valueRef.current, street2: e.target.value });
                }}
                value={valueRef.current.street2}
            />
            <SInputsSpacer />
            <SInputFormItemLabel $disabled={props.disabled}>
                <FormattedMessage {...messages.city} />
            </SInputFormItemLabel>
            <Input
                disabled={props.disabled}
                placeholder={intl.formatMessage(messages.enterCity)}
                onChange={(e) => {
                    valueRef.current.city = e.target.value;
                    props.onChange?.({ ...valueRef.current, city: e.target.value });
                }}
                value={valueRef.current.city}
            />
            <SInputsSpacer />
            <SInputFormItemLabel $disabled={props.disabled}>
                <FormattedMessage {...messages.stateProvinceRegion} />
            </SInputFormItemLabel>
            <Input
                disabled={props.disabled}
                placeholder={intl.formatMessage(messages.enterState)}
                onChange={(e) => {
                    valueRef.current.state = e.target.value;
                    props.onChange?.({ ...valueRef.current, state: e.target.value });
                }}
                value={valueRef.current.state}
            />
            <SInputsSpacer />
            <SInputFormItemLabel $disabled={props.disabled}>
                <FormattedMessage {...messages.zipCode} />
            </SInputFormItemLabel>
            <Input
                disabled={props.disabled}
                placeholder={intl.formatMessage(messages.enterZipCode)}
                onChange={(e) => {
                    valueRef.current.zip = e.target.value;
                    props.onChange?.({ ...valueRef.current, zip: e.target.value });
                }}
                value={valueRef.current.zip}
            />
            <SInputsSpacer />
            <SInputFormItemLabel $disabled={props.disabled}>
                <FormattedMessage {...messages.country} />
            </SInputFormItemLabel>
            <Select
                placeholder={intl.formatMessage(messages.selectCountry)}
                options={countryOptions}
                onChange={(value) => {
                    // TODO: help center build fails due to allowed number type for `key` in TSelectOption
                    const selectedCountryCode = value as string | undefined;

                    valueRef.current.country = selectedCountryCode;
                    props.onChange?.({ ...valueRef.current, country: selectedCountryCode });
                }}
                value={valueRef.current.country}
            />
        </>
    );
});

export default AddressInput;

// #region Styles

const SInputsSpacer = styled.div`
    padding-top: var(--form-gap);
`;

const SInputFormItemLabel = styled.div<{ $disabled?: boolean }>`
    font-size: 1.3rem;
    padding-bottom: 0.8rem;
    font-weight: 550;
    color: ${(p) => (p.$disabled ? p.theme.color.grey8 : p.theme.color.grey9)};
`;

// #endregion

// #region Types

export type TAddressInputProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'onChange'> & {
    disabled?: boolean;
    value?: CustomFieldAddressValue | null;
    onChange?: (value?: CustomFieldAddressValue) => void;
};

export type CustomFieldAddressValue = {
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('AddressInput', async () => {
        // Disabled because of <Select /> context issue
        // const { render } = await import(/* @vite-ignore */ '@test');

        it.todo('matches snapshot');
    });
}

// #endregion
